import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { postVideo, updateVideo } from '../../Redux/Reducers/VideoReducer';

function VideoAddForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useDispatch();
  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [isPrefilling, setIsPrefilling] = useState(false);
  const videoUrl = watch('videoUrl');

  const token = localStorage.getItem('astroVani-admin-token');

  useEffect(() => {
    if (id) {
      setIsPrefilling(true);
      const fetchVideo = async () => {
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_BASEURL}/admin/video/${id}`,
            {
              headers: {
                Authorization: token,
                'Content-Type': 'application/json',
              },
            }
          );
          const videoData = response?.data?.data;
          if (videoData) {
            setValue('title', videoData?.title || '');
            setValue('videoUrl', videoData?.videoUrl || '');
          }
        } catch (error) {
          toast.error('Failed to fetch video details');
        } finally {
          setIsPrefilling(false);
        }
      };

      fetchVideo();
    }
  }, [id, setValue, token]);

  // Extract YouTube video ID from URL
  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=|shorts\/)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2] && match[2].length === 11) ? match[2] : null;
  };

  // Get embed URL for preview
  const getEmbedUrl = (url) => {
    if (!url) return null;
    const videoId = getYouTubeVideoId(url);
    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}`;
    }
    // If already an embed URL, return as is
    if (url.includes('youtube.com/embed')) {
      return url;
    }
    return null;
  };

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      if (id) {
        const videoData = {
          "title": data.title,
          "videoUrl": data.videoUrl,
        }
        await dispatch(updateVideo({ id, videoData })).unwrap();
        toast.success('Video updated successfully!');
      } else {
        await dispatch(postVideo(data)).unwrap();
        toast.success('Video added successfully!');
      }
      navigate('/video-list');
    } catch (error) {
      toast.error(error || (id ? 'Failed to update video' : 'Failed to add video'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="card-body">
        {isPrefilling ? (
          <p>Loading...</p>
        ) : (
          <form className="row g-4" onSubmit={handleSubmit(onSubmit)}>
            <div className="col-sm-12">
              <label className="form-label">Title <span className="text-danger">*</span></label>
              <input
                className="form-control"
                type="text"
                placeholder="Enter video title"
                {...register('title', { required: 'Title is required' })}
              />
              {errors.title && <span className="text-danger">{errors.title.message}</span>}
            </div>

            <div className="col-sm-12">
              <label className="form-label">Video URL <span className="text-danger">*</span></label>
              <input
                className="form-control"
                type="url"
                placeholder="Enter YouTube video URL (e.g., https://www.youtube.com/shorts/cQtZnqtiPjo)"
                {...register('videoUrl', {
                  required: 'Video URL is required',
                  pattern: {
                    value: /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be|youtube\.com\/shorts)\/.+/,
                    message: 'Please enter a valid YouTube URL'
                  }
                })}
              />
              {errors.videoUrl && <span className="text-danger">{errors.videoUrl.message}</span>}
            </div>
            <div className="col-sm-12 d-flex justify-content-between align-items-center">
              {/* Video Preview */}
              {videoUrl && getEmbedUrl(videoUrl) && (
                <div className="mt-4">
                  <label className="form-label">Video Preview</label>
                  <div className="video-preview">
                    <iframe className="w-100 h-100 rounded-3" src={getEmbedUrl(videoUrl)} title="Video Preview" />
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <div className="mt-4">
                <button type="submit" className="btn btn-primary text-uppercase px-3" disabled={isLoading}>
                  {isLoading ? (id ? 'Updating...' : 'Adding...') : (id ? 'Update' : 'Add')}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary text-uppercase px-3 mx-2"
                  onClick={() => navigate('/video-list')}
                >
                  Cancel
                </button>
              </div>
            </div>

          </form>
        )}
      </div>
    </>
  );
}

export default VideoAddForm;

